import Cookie from 'cookie-universal'

import { COOKIES } from '../constants';
import googleAnalyticsEvents from './googleAnalyticsEvents';
import metaRouterEvents from './metaRouterEvents';
import {
    AttemptAndUserIdData,
    AttemptType,
    CartType,
    CartViewedData,
    CheckoutStartedData,
    CheckoutStepCompletedData,
    PageLoadedData,
    PaymentInfoEnteredData,
    PaymentType,
    ProductAnalyticsFormat,
    ShippingInfoEnteredData,
    ProductAndCurrencyData,
    MetaRouterCustomTrackingEvents,
    AnalyticsCartActions,
    GoogleDataLayerEvents,
    OrderCompletedData,
    RemindMeData,
    PageErrorData,
} from './types';

const cookies = Cookie();

const returnLangOrLocation = (type: 'lang' | 'location') => {
  let locale = cookies.get(COOKIES.LOCALE);

  switch (locale) {
    case "en-gb":
      return type === "location" ? "en" : "gb";
    case "int":
      return type === "location" ? "en" : cookies.get(COOKIES.MCRCCODE) ?? "dk";
    case "fi-fi":
      return type === "location" ? "fi" : "fi";
    case "sv-se":
      return type === "location" ? "sv" : "se";
    case "no-no":
      return type === "location" ? "no" : "no";
    case "en-us":
        return type === "location" ? "en" : "us";
    default:
      return 'n/a';
  }
}

const returnCountry = () => cookies.get(COOKIES.COUNTRY);
const returnUserId = () => cookies.get(COOKIES.USER_DATA)?.u;
const returnUserLoggedIn = () => cookies.get(COOKIES.USER);
const returnCurrency = () => cookies.get(COOKIES.CURRENCY);


const removeSymbolFromPrice = (price: any) => {
    if (!price) return 0;
    if( typeof price === 'number') return price;

    // Remove all non-numeric characters except digits, commas, dots, and minus sign
    const cleanedPrice = price.replace(/[^\d.,-]/g, '');

    // Identify the decimal separator based on the position of commas and dots
    const hasComma = cleanedPrice.includes(',');
    const hasDot = cleanedPrice.includes('.');

    let normalizedPrice = cleanedPrice;

    if (hasComma && hasDot) {
      // Assume last occurrence is the decimal separator
      if (cleanedPrice.lastIndexOf(',') > cleanedPrice.lastIndexOf('.')) {
        // Replace thousand separators
        normalizedPrice = cleanedPrice.replace(/\./g, '').replace(/,/g, '.');
      } else {
        normalizedPrice = cleanedPrice.replace(/,/g, '');
      }
    } else if (hasComma && !hasDot) {
      // Comma is decimal separator
      normalizedPrice = cleanedPrice.replace(/,/g, '.');
    } else {
      // Remove any commas as thousand separators
      normalizedPrice = cleanedPrice.replace(/,/g, '');
    }

    const parsedNumber = parseFloat(normalizedPrice);

    return isNaN(parsedNumber) ? 0 : parsedNumber;
};

export default {
    pageLoaded() {
        const pageLoadedData: PageLoadedData = {
          page_name: document.title,
          page_url: window.location.href,
          referring_url: document.referrer,
          language: returnLangOrLocation('lang'),
          country: returnLangOrLocation('location'),
          user_id: returnUserId(),
          logged_in: returnUserLoggedIn(),
        };

        googleAnalyticsEvents.pageLoaded(pageLoadedData);
        metaRouterEvents.pageLoaded(pageLoadedData);
    },

    signedUp(attempt: AttemptType) {
        const signUpData: AttemptAndUserIdData = {
            attempt,
            user_id: returnUserId()
        }
        googleAnalyticsEvents.signUp(signUpData);
        metaRouterEvents.signedUp(signUpData);
    },

    signedIn(attempt: AttemptType) {
        const signedInData: AttemptAndUserIdData = {
            attempt,
            user_id: returnUserId()
        }
        googleAnalyticsEvents.login(signedInData);
        metaRouterEvents.signedIn(signedInData);
    },

    newsletterSubscribe(attempt: AttemptType) {
        const newsletterSubscribeData: AttemptAndUserIdData = {
            attempt,
            user_id: returnUserId()
        }
        googleAnalyticsEvents.newsletterSubscribe(newsletterSubscribeData);
        metaRouterEvents.newsletterSubscribe(newsletterSubscribeData);
    },

    updateCartItem(action: AnalyticsCartActions, product: ProductAnalyticsFormat) {
        const updateCartItemData: ProductAndCurrencyData = {
            ...product,
            currency: returnCurrency()
        }
        googleAnalyticsEvents.updateCartItem(action, updateCartItemData);
        metaRouterEvents.updateCartItem(action, updateCartItemData);
    },

    cartViewed(value: number, products: ProductAnalyticsFormat[], cartType: CartType) {
        const cartViewedData: CartViewedData = {
            currency: returnCurrency(),
            value: value,
            items: products.map(item => ({...item, currency: returnCurrency()})),
            cartType
        }
        googleAnalyticsEvents.viewCart(cartViewedData);
        metaRouterEvents.cartViewed(cartViewedData);
    },

    checkoutStarted(value: number, coupon: string, products: ProductAnalyticsFormat[]) {
        const checkoutStartedData: CheckoutStartedData = {
            currency: returnCurrency(),
            value: value,
            coupon: coupon,
            items: products.map(item => ({...item, currency: returnCurrency()}))
        }
        googleAnalyticsEvents.beginCheckout(checkoutStartedData);
        metaRouterEvents.checkoutStarted(checkoutStartedData);
    },

    shippingInfoEntered(coupon: string, value: number, deliveryMethod: any, products: ProductAnalyticsFormat[]) {
        const shippingInfoEnteredData: ShippingInfoEnteredData = {
            currency: returnCurrency(),
            value: value,
            coupon: coupon,
            delivery_method: deliveryMethod,
            items: products.map(item => ({...item, currency: returnCurrency()}))
        }
        googleAnalyticsEvents.addShippingInfo(shippingInfoEnteredData);
        metaRouterEvents.shippingInfoEntered(shippingInfoEnteredData);
    },

    paymentInfoEntered(value: number, coupon: string, payment_type: any, products: ProductAnalyticsFormat[]) {
        // payment type is scheme only for credit card so we need to change it before a call to metarouter
        payment_type = payment_type === PaymentType.SCHEME ? PaymentType.CREDIT_CARD : payment_type;

        const paymentInfoEnteredData: PaymentInfoEnteredData = {
            currency: returnCurrency(),
            value: value,
            coupon: coupon,
            payment_type: payment_type,
            items: products.map(item => ({...item, currency: returnCurrency()}))
        }
        googleAnalyticsEvents.addPaymentInfo(paymentInfoEnteredData);
        metaRouterEvents.paymentInfoEntered(paymentInfoEnteredData);
    },

    checkoutStepCompleted(number: number, name: any) {
        const checkoutStepCompletedData: CheckoutStepCompletedData = {
            step_number: number,
            step_name: name
        }
        googleAnalyticsEvents.checkoutStepCompleted(checkoutStepCompletedData);
        metaRouterEvents.checkoutStepCompleted(checkoutStepCompletedData);
    },

    productViewed(product: ProductAnalyticsFormat | any) {
        const productViewedData: ProductAndCurrencyData = {
            ...product,
            currency: returnCurrency()
        }
        googleAnalyticsEvents.viewItem(productViewedData);
        metaRouterEvents.productViewed(productViewedData);
    },

    giftWrapped(action: AnalyticsCartActions, product: ProductAnalyticsFormat) {
        const giftWrappedData: ProductAndCurrencyData = {
            ...product,
            currency: returnCurrency()
        }

        metaRouterEvents.giftWrapped(action === AnalyticsCartActions.ADD ? MetaRouterCustomTrackingEvents.PRODUCT_ADDED : MetaRouterCustomTrackingEvents.PRODUCT_REMOVED, giftWrappedData);
        googleAnalyticsEvents.giftWrapped(action === AnalyticsCartActions.ADD ? GoogleDataLayerEvents.ADD_TO_CART : GoogleDataLayerEvents.REMOVE_FROM_CART, giftWrappedData);
    },

    orderCompleted(transactionId: string, value: number, tax: any, shipping: any, coupon: string, products: ProductAnalyticsFormat[]) {
        const orderCompletedData: OrderCompletedData = {
            transaction_id: transactionId,
            value: removeSymbolFromPrice(value),
            tax: removeSymbolFromPrice(tax),
            shipping: removeSymbolFromPrice(shipping),
            currency: returnCurrency(),
            coupon,
            items: products.map(item => ({...item, currency: returnCurrency()}))
        }
        googleAnalyticsEvents.purchase(orderCompletedData);
        metaRouterEvents.orderCompleted(orderCompletedData);
    },

    productsSearched(search_results: any, search_term: string) {
        const productsSearchedData = {
            search_term: search_term || "",
            search_results
        }
        googleAnalyticsEvents.search(productsSearchedData);
        metaRouterEvents.productsSearched(productsSearchedData);
    },

    remindMe(product_name: string, sku: string, product_category: string, brand: string, variant: any, price: number, product_url: string, discount: number) {
        const remindMeData: RemindMeData = {
            product_name,
            sku,
            product_category,
            brand,
            variant,
            price,
            product_url,
            discount
        }
        googleAnalyticsEvents.remindMe(remindMeData);
        metaRouterEvents.remindMe(remindMeData);
    },

    pageError(errorCode: number) {

        // probabily we wont need this
        const url = window.location.href;
        // This is causing multiple page calls, resulting into redundant calls and slower page load perfomance
        // const http = new XMLHttpRequest();
        // http.open("HEAD", url, false);
        // http.send();

        const pageErrorData: PageErrorData = {
            error_type: errorCode,
            page_url: url
        }

        googleAnalyticsEvents.pageError(pageErrorData);
        metaRouterEvents.pageError(pageErrorData);
    },


    eventListeners() {
        /*
        * Event listeners
        */
        window.addEventListener("load", () => {
            this.pageLoaded();
            // Removed undefined parameter call - pageError requires valid error code
            // this.pageError();
        });
    }
}
